import { Injectable, Inject, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { KeycloakAuthService } from '../core/services/keycloak-auth.service';
import { ApiConfigService } from '../core/services/api-config.service';

export interface PermissionSet {
    Id: string;
    Name: string;
    Permissions: string[];
}

@Injectable({
  providedIn: 'root'
})
export class PermissionSetService {
  private api: AxiosInstance;

  constructor(@Inject(PLATFORM_ID) private platformId: Object, private authService: KeycloakAuthService, private apiConfig: ApiConfigService) {
    this.api = axios.create({
      headers: {
        'Content-Type': 'application/json'
      }
    });

    // Add request interceptor to dynamically set baseURL and include OAuth token
    this.api.interceptors.request.use((config) => {
      // Dynamically construct baseURL on each request to get current tenantId
      if (isPlatformBrowser(this.platformId)) {
        const baseUrl = this.apiConfig.getApiBaseUrl();
        const tenantId = this.apiConfig.getTenantId();
        config.baseURL = tenantId
          ? `${baseUrl}/${tenantId}/permissionset`
          : `${baseUrl}/permissionset`;
      }

      const token = this.authService.accessToken;
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
  }

    async getPermissionSet(permissionSetId: string): Promise<PermissionSet | null> {
        try {
            const response = await this.api.get(`/${permissionSetId}`);
            return response.data;
        } catch (error: any) {
            console.error(error);
            throw error;
        }
    }

    async getPermissionsOfPermissionSet(permissionSetId: string): Promise<Record<string, string[]>> {
        try {
            const response = await this.api.get(`/${permissionSetId}/permissions`);
            return response.data;
        } catch (error: any) {
            console.error(error);
            throw error;
        }
    }

    async getAllPermissionSets(): Promise<PermissionSet[] | null> {
        try {
            const response = await this.api.get('/');
            return response.data;
        } catch (error: any) {
            console.error(error);
            throw error;
        }
    }

    async updatePermissionSet(permissionSet: PermissionSet): Promise<AxiosResponse | null> {
        try {
            const response = await this.api.put(`/${permissionSet.Id}`, permissionSet);
            return response;
        } catch (error: any) {
            console.error(error);
            throw error;
        }
    }
    
    async deletePermissionSet(permissionSetId: string): Promise<AxiosResponse | null> {
        try {
            const response = await this.api.delete(`/${permissionSetId}`);
            return response;
        } catch (error: any) {
            console.error(error);
            throw error;
        }
    }

    async createPermissionSet(permissionSet: Omit<PermissionSet, 'Id'>): Promise<string> {
        try {
            const response = await this.api.post('/', permissionSet);
            return response.data;
        } catch (error: any) {
            console.error(error);
            throw error;
        }
    }
}
    
